import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { ProcedureFigure, ProcedureFigureGrid } from "@/components/site/ProcedureFigure";
import { SectionCta } from "@/components/site/SectionCta";

import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { PHYSICIAN_REF, SETTING_ONE_NIGHT_ES } from "@/lib/medical-schema";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";
import sleevePostoperativeDiet from "@/assets/Sleeve_Postoperative_Diet.pdf.asset.json";
import { ImageLightbox } from "@/components/site/ImageLightbox";
import sleeveEndoscopyCheckEs from "@/assets/sleeve-endoscopy-check-es.png.asset.json";
import sleeveEndoscopyCheckEs480 from "@/assets/sleeve-endoscopy-check-es-480.webp.asset.json";
import sleeveEndoscopyCheckEs800 from "@/assets/sleeve-endoscopy-check-es-800.webp.asset.json";
import sleeveEndoscopyCheckEs1200 from "@/assets/sleeve-endoscopy-check-es-1200.webp.asset.json";

/** Spanish translation of the English sleeve FAQ, kept in sync with /services/sleeve. */
const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Se desvía alguna parte del estómago o del intestino?",
    a: "No. La manga gástrica extrae una parte del estómago, pero no desvía el intestino. La comida sigue su recorrido normal. Por eso la absorción de nutrientes se conserva mejor que después de un bypass gástrico.",
  },
  {
    q: "¿Qué es mejor, la manga o el bypass?",
    a: "Ambas son excelentes operaciones, con una pérdida de peso parecida y tasas similares de remisión de la diabetes. La manga es más sencilla y tiene menos complicaciones. El bypass suele ser la mejor opción si usted tiene reflujo importante o ciertas enfermedades metabólicas. La respuesta correcta es individual.",
  },
  {
    q: "¿Se puede revertir la manga gástrica?",
    a: "No. Una parte del estómago se extrae de forma permanente, así que la manga no se puede deshacer. Sin embargo, más adelante se puede convertir en otra operación si llegara a ser necesario.",
  },
  {
    q: "¿Cuánto dura la recuperación después de la manga gástrica?",
    a: "La mayoría de los pacientes pasa una noche en el hospital y vuelve a su actividad normal en unas cuatro semanas. Se empieza con una dieta líquida y se avanza a purés, alimentos blandos y comida regular durante las ocho semanas siguientes.",
  },
  {
    q: "¿El seguro cubre la manga gástrica?",
    a: "Muchas aseguradoras cubren la gastrectomía en manga cuando se cumplen sus criterios médicos, por lo general un IMC de 40 o más, o de 35 o más junto con una enfermedad relacionada con el peso. Nuestro equipo verifica sus beneficios y lo acompaña en la autorización.",
  },
  {
    q: "¿Qué dieta debo seguir después de la manga gástrica?",
    a: "La alimentación avanza semana a semana durante ocho semanas: líquidos claros, luego líquidos completos y batidos de proteína, después purés y alimentos blandos, y por último comida sólida regular. Nuestra nutricionista lo acompaña en cada etapa.",
  },
];

export const Route = createFileRoute("/es/manga-gastrica")({
  head: () => {
    const base = esHead("sleeve");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                ...esWebPageSchema("sleeve"),
                "@type": "MedicalWebPage",
                reviewedBy: PHYSICIAN_REF,
                mainEntity: { "@id": `${SITE}/es/manga-gastrica#procedimiento` },
              },
              ...procedureImageObjects(ES_PAGE_MEDIA["/es/manga-gastrica"], "es", `${SITE}/es/manga-gastrica`),
              {
                "@type": "ImageObject",
                contentUrl: `${SITE}${sleeveEndoscopyCheckEs.url}`,
                url: `${SITE}${sleeveEndoscopyCheckEs.url}`,
                caption: "Control por endoscopia después de completar la manga: es la mejor manera de confirmar que no hay nada que corregir. También permite comprobar que no haya fugas.",
                width: 1461,
                height: 814,
                inLanguage: "es-US",
              },
              {
                "@type": "MedicalProcedure",
                "@id": `${SITE}/es/manga-gastrica#procedimiento`,
                name: "Gastrectomía en manga (manga gástrica)",
                alternateName: "Sleeve gastrectomy",
                description:
                  "Cirugía laparoscópica que reduce el estómago a un tubo estrecho para disminuir la cantidad de comida y las hormonas del hambre.",
                bodyLocation: "estómago",
                howPerformed:
                  "Por laparoscopía, mediante varias incisiones pequeñas, se extrae aproximadamente el 75 al 80 por ciento del estómago y se deja un tubo estrecho con forma de manga." + " " + SETTING_ONE_NIGHT_ES,
                url: `${SITE}/es/manga-gastrica`,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/manga-gastrica#faq`,
                inLanguage: "es",
                mainEntity: FAQS.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsSleeve,
});

function EsSleeve() {
  return (
    <EsPageShell
      page="sleeve"
      title="Cirugía de manga gástrica en el norte de Virginia"
      intro="La manga gástrica es la operación de pérdida de peso más realizada en los Estados Unidos y en nuestro consultorio. Se hace por laparoscopía, con incisiones pequeñas, y no desvía el intestino."
      banner={<MedicalReviewBanner lang="es" className="mt-4" />}
    >
      <EsSection id="resumen" heading="Qué es la manga gástrica">
        <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-start">
          <div className="space-y-3">
            <p>
              La manga gástrica es la cirugía de pérdida de peso más común del mundo. También se la
              conoce como gastrectomía vertical en manga o, simplemente, «la manga».
            </p>
            <p>
              La operación extrae cerca del 80 por ciento del estómago y deja un tubo delgado con
              forma de banana. Ese estómago más pequeño contiene mucha menos comida, de modo que
              limita la cantidad que usted puede comer y produce una sensación de saciedad con
              porciones sorprendentemente pequeñas.
            </p>
            <p>
              La cirugía también reduce el apetito por medio de cambios hormonales. La porción de
              estómago que se retira produce la mayor parte de la grelina, la principal hormona del
              hambre. Cuando la grelina baja, la mayoría de los pacientes nota menos apetito a lo
              largo del día, lo que se suma al efecto del estómago más pequeño.
            </p>
            <p>
              El resultado es una pérdida de peso importante y duradera, además de una mejoría real
              en las enfermedades relacionadas con la obesidad.
            </p>
          </div>

          <ProcedureFigure
            media="sleeveDiagramEs"
            lang="es"
            lightbox
            className="flex flex-col items-center md:sticky md:top-4"
            imgClassName="w-full h-auto max-w-md rounded-lg"
            captionClassName="mt-2 text-center text-sm text-foreground/70"
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 rounded-lg border border-border p-4 sm:grid-cols-3">
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">~70 %</p>
            <p className="mt-1 text-sm text-foreground/70">
              del exceso de peso perdido, en promedio, en los primeros uno o dos años
            </p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">~80 %</p>
            <p className="mt-1 text-sm text-foreground/70">
              del estómago se extrae para formar la manga
            </p>
          </div>
          <div className="text-center">
            <p className="font-serif text-2xl text-brand-mid">N.º 1</p>
            <p className="mt-1 text-sm text-foreground/70">
              la operación de pérdida de peso más realizada en el mundo
            </p>
          </div>
        </div>
      </EsSection>

      <EsSection id="candidatos" heading="¿Quién es candidato para la manga gástrica?">
        <p>
          Ofrecemos la manga a adultos con un índice de masa corporal (IMC) de 35 o más. Un IMC de
          30 o más también califica si existe una enfermedad relacionada con la obesidad. En
          pacientes de ascendencia asiática oriental, esos números bajan a 32.5 y 27.5.
        </p>
        <p>
          Pero el IMC no lo es todo. Revisamos su historia clínica, lo que ya ha intentado con dieta
          y ejercicio, y si está listo para cambiar su forma de comer de manera definitiva. Vea
          nuestros{" "}
          <Link
            to="/es/requisitos-para-cirugia"
            className="font-medium text-brand-logo hover:underline"
          >
            requisitos completos para la cirugía bariátrica
          </Link>{" "}
          con los umbrales de IMC, los criterios de enfermedades asociadas y la guía sobre la edad.
        </p>
        <p>
          Cada plan de seguro tiene requisitos propios; puede revisarlos en nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            cobertura de seguro
          </Link>
          .
        </p>
        <p>
          Una advertencia importante: la manga puede provocar o empeorar el reflujo. Si usted ya
          tiene reflujo importante (ERGE), un bypass gástrico puede convenirle más, porque suele
          mejorar el reflujo en lugar de empeorarlo. El Dr. Fitzer analizará este y otros puntos
          con usted.
        </p>
        <p>
          Si prefiere empezar sin cirugía, también ofrecemos un{" "}
          <Link
            to="/es/perdida-de-peso-con-medicamentos"
            className="font-medium text-brand-logo hover:underline"
          >
            programa con medicamentos
          </Link>{" "}
          supervisado por un médico.
        </p>
        <p className="flex flex-wrap justify-center gap-3 pt-1">
          <Link
            to="/es/contacto"
            className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Pedir una Cita para Hablar de la Manga Gástrica
          </Link>
          <Link
            to="/es/seguro-para-cirugia-bariatrica"
            className="inline-flex items-center rounded-full border border-brand-deep px-6 py-3 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Cobertura de seguro para la manga gástrica
          </Link>
        </p>
      </EsSection>


      <EsSection id="como-funciona" heading="En qué consiste el procedimiento">
        <p>
          El Dr. Fitzer realiza la manga por laparoscopía, con el paciente bajo anestesia general.
          Este abordaje mínimamente invasivo evita el corte largo de la cirugía abierta: trabaja a
          través de varias incisiones pequeñas con una cámara y con instrumentos delgados. Las
          incisiones pequeñas significan una recuperación más rápida y menos problemas de herida.
          La mayoría de los casos dura entre 35 y 75 minutos.{" "}
          <a
            href="https://www.youtube.com/watch?v=HIuVAjZQe-o"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-brand-logo hover:underline"
          >
            Puede ver al Dr. Fitzer realizar una manga en nuestro canal de YouTube.
          </a>
        </p>

        <p>
          Durante la operación se extrae la porción externa y distensible del estómago y se sella el
          borde restante, lo que crea la manga estrecha.
        </p>
        <p>
          Después de crear la manga, el Dr. Fitzer realiza una endoscopía. Se cambia de guantes y bata
          e introduce un gastroscopio en el esófago. Lo usa para inspeccionar toda la manga,
          centímetro a centímetro. Así puede asegurarse de que no haya nada que requiera atención
          (como un sangrado, un pliegue, etc.). La siguiente imagen muestra cómo se ve el estómago
          durante la endoscopía.
        </p>
        <figure className="mt-2 md:max-w-[70%] md:mx-auto">
          <ImageLightbox
            fullSrc={sleeveEndoscopyCheckEs.url}
            fullWidth={1461}
            fullHeight={814}
            alt="Vista laparoscópica de la manga gástrica recién creada durante la endoscopia de control, con el abdomen lleno de agua estéril"
            caption="Control por endoscopia después de completar la manga: es la mejor manera de confirmar que no hay nada que corregir. También permite comprobar que no haya fugas. Es similar a cómo se detectan las fugas en las llantas de los carros. La manga se llena de aire y luego la sumergimos en agua. (El agua que se ve se bombeó para este propósito; es agua especial, estéril.) Si no hay burbujas visibles, no hay fuga."
          >
            <img
              src={sleeveEndoscopyCheckEs800.url}
              srcSet={`${sleeveEndoscopyCheckEs480.url} 480w, ${sleeveEndoscopyCheckEs800.url} 800w, ${sleeveEndoscopyCheckEs1200.url} 1200w`}
              sizes="(min-width: 768px) 70vw, calc(100vw - 2rem)"
              alt="Vista laparoscópica de la manga gástrica recién creada durante la endoscopia de control, con el abdomen lleno de agua estéril"
              width={1461}
              height={814}
              loading="lazy"
              decoding="async"
              className="block w-full h-auto rounded-lg"
            />
          </ImageLightbox>
          <figcaption className="mt-2 text-center text-sm text-muted-foreground">
            Control por endoscopia después de completar la manga: es la mejor manera de confirmar que no hay nada que corregir. También permite comprobar que no haya fugas. Es similar a cómo se detectan las fugas en las llantas de los carros. La manga se llena de aire y luego la sumergimos en agua. (El agua que se ve se bombeó para este propósito; es agua especial, estéril.) Si no hay burbujas visibles, no hay fuga.
          </figcaption>
        </figure>
        <p>
          El intestino delgado no se toca. Como la comida sigue su recorrido normal, el cuerpo
          absorbe los nutrientes igual que antes, y el riesgo de deficiencias nutricionales es
          menor que con las operaciones que desvían el intestino.
        </p>
        <p>
          Algo que conviene tener claro: la manga no es reversible. La porción de estómago que se
          extrae se pierde de forma permanente. Sin embargo, más adelante se puede convertir en otra
          operación si llegara a ser necesario.
        </p>
        <p>
          ¿Está comparando la manga con el bypass? Vea nuestra{" "}
          <Link to="/es/bypass-o-manga-gastrica" className="font-medium text-brand-logo hover:underline">
            comparación lado a lado del bypass gástrico y la manga gástrica
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="como-ayuda" heading="Por qué funciona">
        <p>
          La manga gástrica actúa de dos maneras. Primero, reduce físicamente la cantidad de
          comida que cabe en el estómago, así que se siente satisfecho con porciones mucho más
          pequeñas. Segundo, la parte del estómago que se extrae produce la mayor parte de la
          grelina, una de las principales hormonas del hambre. Al retirarla, muchos pacientes
          notan que el apetito y los antojos disminuyen de forma notable.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="¿Cuánto peso se pierde con la manga gástrica?">
        <p>
          Los pacientes de manga pierden más peso que los de banda gástrica, aunque algo menos que
          los de bypass gástrico. Entre los pacientes de Virginia Bariatric, la pérdida del exceso
          de peso promedia más del 70 por ciento desde el primer año hasta el noveno. Abajo verá una
          gráfica del porcentaje promedio de exceso de peso perdido por los pacientes reales de
          manga gástrica del Dr. Fitzer, del año uno al año nueve después de la cirugía. Fue
          generada automáticamente por el sistema de expediente clínico electrónico del consultorio
          (Baritrax360). Se enviaron datos idénticos al registro MBSAQIP.
        </p>

        <ProcedureFigure media="sleeveGraph" lang="es" lightbox className="mt-2" imgClassName="block w-full h-auto rounded-lg" />

        <p>
          Los resultados varían de un paciente a otro y dependen en gran medida de los cambios de
          alimentación, la actividad física y el seguimiento a largo plazo.
        </p>
        <SectionCta to="/es/requisitos-para-cirugia" label="cta_sleeve_after_results_es">Vea si cumple los requisitos para la manga</SectionCta>
      </EsSection>

      <EsSection id="recuperacion-de-peso" heading="Recuperación de peso después de la cirugía">
        <p>
          Si ya pasaron varios años desde su manga y el peso está regresando, la biología detrás de
          la recuperación de peso, los medicamentos GLP-1 después de la cirugía y las opciones de
          revisión se explican en nuestra{" "}
          <a
            href="/resources/managing-weight-regain-after-bariatric-surgery"
            hrefLang="en"
            className="font-medium text-brand-logo hover:underline"
          >
            guía para manejar la recuperación de peso después de la cirugía bariátrica
          </a>{" "}
          (en inglés). En español, vea nuestra página de{" "}
          <Link to="/es/cirugia-de-revision" className="font-medium text-brand-logo hover:underline">
            cirugía de revisión
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="tecnica" heading="La formación del cirujano importa">

        <p>
          Los grandes registros nacionales (por ejemplo, el MBSAQIP) informan cuál es la pérdida de
          peso promedio a largo plazo con la cirugía de manga en los Estados Unidos. El promedio
          nacional se mantiene en el 60 por ciento, aunque varía de un consultorio a otro. El factor
          importante parece ser el enfoque del cirujano que opera.
        </p>
        <p>
          Más de la mitad de las mangas que se realizan cada año en los Estados Unidos están a cargo
          de cirujanos sin formación formal de fellowship en cirugía de pérdida de peso. Para ellos,
          la manga puede ser una parte pequeña de su volumen de casos.
        </p>
        <p>
          Cuando la cirugía de manga se hace sin esa formación, el estómago suele recibir un recorte
          demasiado cauteloso en lugar de una verdadera reducción de volumen. Estos son algunos
          ejemplos de pacientes con mangas excesivamente amplias que fueron referidos al Dr. Fitzer
          para una cirugía de revisión:
        </p>

        <ProcedureFigureGrid
          items={["sleeveAmateur1", "sleeveAmateur2", "sleeveAmateur3"]}
          lang="es"
        />

        <p>
          El otro tipo de cirujano de manga es el cirujano bariátrico. Los cirujanos bariátricos
          suelen tener uno o más años de formación formal de fellowship, y su práctica se dedica
          exclusivamente a la medicina y la cirugía bariátrica.
        </p>
        <p>
          El Dr. Fitzer es de este tipo. Completó un fellowship de un año en cirugía bariátrica en
          la Universidad de Missouri entre 2005 y 2006. Desde entonces, casi el 100 por ciento de su
          volumen anual de casos han sido procedimientos bariátricos.
        </p>
        <p>
          En un cirujano bariátrico se puede confiar para una reducción adecuada del estómago. Estos
          son algunos estudios de imagen de pacientes de manga del propio Dr. Fitzer (sin
          información que permita identificarlos):
        </p>

        <ProcedureFigureGrid
          items={["sleeveFitzer1", "sleeveFitzer2", "sleeveFitzer3"]}
          lang="es"
        />

        <p>
          Cuando alguien pierde muy poco peso después de una manga, o recupera casi todo lo perdido,
          la manga puede ser la responsable. La buena noticia es que una manga que no funciona bien
          suele poder repararse sin mayor dificultad. Vea{" "}
          <Link to="/es/cirugia-de-revision" className="font-medium text-brand-logo hover:underline">
            cirugía de revisión
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="beneficios" heading="Beneficios de salud más allá del peso">
        <p>
          Bajar de peso con la manga suele mejorar las enfermedades relacionadas con la obesidad y,
          en muchos casos, las resuelve por completo. La diabetes tipo 2, la presión arterial alta,
          el colesterol elevado, la apnea del sueño y el dolor articular tienden a mejorar.
        </p>
        <p>
          El control del azúcar en la sangre a menudo mejora a los pocos días de la cirugía, mucho
          antes de haber bajado mucho de peso. Ese efecto temprano se debe a cambios favorables en
          las hormonas intestinales.
        </p>
        <p>La manga también ofrece varias ventajas prácticas frente a operaciones más complejas:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Es una operación más sencilla que el bypass gástrico, con menos tiempo de anestesia.</li>
          <li>La estancia en el hospital suele ser de una sola noche.</li>
          <li>Las tasas de complicaciones son bajas.</li>
          <li>La absorción de nutrientes se mantiene casi normal, porque el intestino delgado no se toca.</li>
          <li>El estómago y el intestino siguen siendo accesibles con endoscopio, algo que importa si alguna vez lo necesita.</li>
        </ul>
      </EsSection>


      <EsSection id="recuperacion" heading="Recuperación después de la manga gástrica">
        <p>
          La estancia hospitalaria es corta. La mayoría de los pacientes vuelve a casa después de
          una noche y retoma sus actividades normales en unas cuatro semanas.
        </p>
        <p>
          La dieta avanza por etapas. Se empieza con líquidos claros en el hospital, se pasa a
          líquidos y batidos de proteína en casa durante la primera semana, y luego una semana de
          purés y alimentos blandos. Hacia las ocho semanas se vuelve a la comida sólida regular, en
          porciones mucho más pequeñas que antes de la cirugía.
        </p>
        <p>
          Las vitaminas diarias comienzan el mismo día en que regresa a casa.{" "}
          <a
            href="https://virginiabariatric.com/__l5e/assets-v1/0a79aabf-27ff-4fa0-843d-ba9bb497b736/Nutritional_Supplement_Guidelines.pdf"
            target="_blank"
            rel="noopener noreferrer"
            type="application/pdf"
            className="font-medium text-brand-logo hover:underline"
          >
            Los suplementos vitamínicos recomendados se pueden ver o descargar aquí
          </a>{" "}
          (documento en inglés). Durante el primer año verá al Dr. Fitzer y a su nutricionista con
          regularidad.
        </p>

        <p>
          Muchos pacientes descubren que el cambio a comidas pequeñas es más llevadero de lo que
          temían. Aun así, el trabajo personalizado con un especialista en hábitos alimentarios hace
          una diferencia real, y nuestro equipo lo acompaña en todo el proceso.
        </p>
      </EsSection>

      <EsSection id="riesgos" heading="Riesgos y efectos secundarios">
        <p>
          La gastrectomía en manga tiene un buen historial de seguridad y las complicaciones graves
          son poco frecuentes.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Riesgos quirúrgicos generales: sangrado, infección, coágulos y, con poca frecuencia,
            fuga en la línea de grapas durante el primer mes.
          </li>
          <li>
            Efectos tempranos leves y pasajeros: náuseas, cansancio y molestia en las incisiones,
            que suelen ceder en las primeras semanas.
          </li>
          <li>
            Vitaminas de por vida: aunque la absorción no cambia mucho, se recomienda tomar
            suplementos y hacerse análisis de sangre periódicos.
          </li>
          <li>
            Complicaciones a largo plazo como la recuperación de peso son posibles; el seguimiento
            continuo es la mejor prevención.
          </li>
        </ul>

        <p>
          Las complicaciones a largo plazo son poco frecuentes, salvo una: entre el 10 y el 20 por
          ciento de los pacientes presenta reflujo nuevo o peor.
        </p>
        <p id="reflujo-y-la-manga">
          ¿Por qué la manga gástrica puede causar reflujo? Las imágenes siguientes ayudan a
          explicarlo.
        </p>

        <ProcedureFigureGrid
          items={["sleeveRefluxNormal", "sleeveRefluxPostSleeve"]}
          lang="es"
          className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2"
        />

        <p className="text-center text-sm italic text-foreground/70">
          Observe que el estómago normal contiene más líquido y, aun así, el nivel queda por debajo
          de la entrada del esófago. En el estómago con manga, el líquido queda justo sobre la
          entrada del esófago. Mientras el paciente permanece acostado boca arriba, la gravedad
          empuja el líquido ácido hacia el esófago. Si la presión del esfínter esofágico inferior
          es baja, aparecen el reflujo y la acidez.
        </p>
        <p>
          Es normal que el esófago tenga algo de exposición al ácido del estómago. Los síntomas de
          reflujo y el daño esofágico suelen aparecer solo cuando la duración diaria de esa
          exposición supera un umbral del 4 al 6 por ciento.
        </p>
        <p>
          Por el cambio anatómico, los pacientes con manga que duermen boca arriba a menudo tienen
          reflujo por la noche. Incluso un poco de exposición nocturna adicional puede llevarlos
          por encima del umbral de los síntomas.
        </p>
        <p>
          Una hernia hiatal con frecuencia contribuye al reflujo después de una manga, y cuando se
          encuentra una, se repara en la misma operación — vea{" "}
          <Link to="/es/reparacion-de-hernia-hiatal" className="text-brand-deep underline hover:no-underline">reparación de hernia hiatal</Link>.
        </p>
        <SectionCta to="/es/contacto" label="cta_sleeve_after_reflux_es">Pregunte qué cirugía conviene para su reflujo</SectionCta>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Cirugía y seguimiento cerca de Herndon, Reston y Fairfax">
        <p>
          La gastrectomía en manga se realiza en INOVA Fair Oaks Hospital en Fairfax, y la mayoría de los pacientes se van a casa después de una noche. Las visitas preoperatorias y el seguimiento son en nuestro consultorio de Herndon, a corta distancia de Reston, el condado de Loudoun y el oeste de Fairfax. Los pacientes que viven más lejos pueden comenzar con una consulta por telesalud.
        </p>
        <p>
          ¿Está comparando opciones? Vea <Link to="/es/bypass-gastrico" className="text-brand-deep underline hover:no-underline">bypass gástrico en el Norte de Virginia</Link>, o <Link to="/es/gastroplastia-endoscopica-en-manga" className="text-brand-deep underline hover:no-underline">gastroplastia endoscópica en manga (ESG) en el Norte de Virginia</Link> si prefiere un método sin cirugía.
        </p>
        <p>
          ¿Preguntas sobre la manga gástrica? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_sleeve_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        {FAQS.map((f) => (
          <div key={f.q}>
            <h3 className="mt-3 font-semibold text-foreground">{f.q}</h3>
            <p className="mt-1">{f.a}</p>
          </div>
        ))}
        <p>
          <a
            href={sleevePostoperativeDiet.url}
            download={sleevePostoperativeDiet.original_filename}
            target="_blank"
            rel="noopener noreferrer"
            type="application/pdf"
            className="font-medium text-brand-logo hover:underline"
          >
            Las instrucciones de dieta específicas para la manga se pueden consultar aquí
          </a>{" "}
          (documento en inglés).
        </p>
      </EsSection>

      <EsSection id="mas-informacion" heading="Más información en español">
        <p>
          ¿No va a usar el seguro? Consulte{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-manga-gastrica"
            className="font-medium text-brand-logo hover:underline"
          >
            cuánto cuesta la manga gástrica
          </Link>{" "}
          con pago propio: un estimado de $19,200, que cubre el honorario del cirujano, un día de
          hospital en INOVA Fair Oaks Hospital, un estimado de anestesia y un año de seguimiento
          de rutina con el Dr. Fitzer.
        </p>
        <SectionCta to="/es/cobertura-de-seguro" label="cta_sleeve_after_insurance_es">Revise la cobertura de su manga gástrica</SectionCta>
        <p>

          ¿Está considerando la manga gástrica? El primer paso es una consulta. Revisamos su
          historia clínica y sus metas, y juntos decidimos si la manga es la opción indicada para
          usted.
        </p>
        <p className="flex flex-wrap justify-center gap-3 pt-1">
          <Link
            to="/es/bypass-o-manga-gastrica"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Bypass o manga: comparación
          </Link>
          <Link
            to="/es/cirugia-de-revision"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Cirugía de revisión
          </Link>
          <Link
            to="/es/cobertura-de-seguro"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Cobertura de seguro
          </Link>
        </p>
      </EsSection>

      <EsSection id="referencias" heading="Referencias médicas seleccionadas">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground/70">
          <li>
            Han Y, et al. Comparative analysis of weight loss and resolution of comorbidities between
            laparoscopic sleeve gastrectomy and Roux-en-Y gastric bypass: a systematic review and
            meta-analysis based on 18 studies. Int J Surg. 2020;76:101-110.
            doi:10.1016/j.ijsu.2020.02.035
          </li>
          <li>
            Guan B, et al. Mid-long-term Revisional Surgery After Sleeve Gastrectomy: a Systematic
            Review and Meta-analysis. Obes Surg. 2019;29:1965-1975. doi:10.1007/s11695-019-03842-3
          </li>
        </ol>
      </EsSection>
    </EsPageShell>
  );
}
